import React from "react";
import dayjs from "utils/dayjs";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CircularProgress,
  Tooltip,
  Typography,
} from "@mui/material";
import { ActionTray, Chip, IconButton, StatusGlyph } from "@outpave/ds";
import { HugeiconsIcon } from "@hugeicons/react";
import { Image01Icon } from "@hugeicons-pro/core-stroke-standard";
import {
  Delete02Icon,
  Download04Icon,
  PencilEdit01Icon,
} from "@hugeicons-pro/core-stroke-rounded";
import { useDisplayAttachments } from "hooks/useDisplayAttachments";
import { formatCashFull } from "utils/formatCash";
import {
  Receipt,
  isReceiptScanFailed,
  isReceiptProcessing,
  isReceiptTransactionDateMissing,
} from "utils/receipts/receiptFormat";
import PdfIcon from "assets/images/pdf-icon.svg";
import { fullName } from "utils/fullName";
import UserAvatar from "components/common/users/UserAvatar";

/**
 * Selector hooks, not utility classes: the card owns the reveal, the child owns
 * receiving it. Constants because the pair is a contract split across an `sx`
 * selector and a `className`, and nothing type-checks that they still match.
 */
const REVEAL = "receipt-card-reveal";
const CONNECT_REVEAL = "receipt-card-connect";

interface ReceiptLibraryCardProps {
  receipt: Receipt;
  onOpen: () => void;
  /** Every affordance is gated on its handler, the way the package does it. */
  onConnect?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  /** Manager and above; on My Outpave every receipt is already yours. */
  showUploader?: boolean;
}

/**
 * One receipt tile for both library grids. The card is where a whole receipt is
 * deleted, which is why the image count is on it: the delete takes every image
 * with it, and the count is what says so.
 */
const ReceiptLibraryCard: React.FC<ReceiptLibraryCardProps> = ({
  receipt,
  onOpen,
  onConnect,
  onEdit,
  onDelete,
  showUploader = false,
}) => {
  const { t } = useTranslation();
  // Downloading needs no permission, unlike editing and deleting, so it is not
  // handler-gated: anyone who can see the receipt can take a copy of it.
  const { downloadAllAssetsAsZip } = useDisplayAttachments({
    allAttachments: receipt.assets ?? [],
  });
  const scanFailed = isReceiptScanFailed(receipt);
  const processing = isReceiptProcessing(receipt);
  // StatusGlyph is keyed off the untranslated label, so `t` applies only to
  // what renders.
  const scanLabel = processing
    ? receipt.processingStatus === "QUEUED"
      ? "ReceiptProcessingQueued"
      : "ReceiptProcessingScanning"
    : scanFailed
      ? "Scan Failed"
      : "Unmatched";
  const scanTone = processing ? "neutral" : scanFailed ? "negative" : "warning";
  const firstAsset = receipt.assets?.[0];
  const isPdf = firstAsset?.extension?.toLowerCase() === "pdf";
  const hasImage = !isPdf && Boolean(firstAsset?.assetUrl);
  const hasAmount = Boolean(receipt.amount) && receipt.amount > 0;
  const dateMissing = isReceiptTransactionDateMissing(receipt.transactionDate);
  const assetCount = receipt.assets?.length ?? 0;
  const uploader = showUploader ? receipt.user : null;

  const stop = (action?: () => void) => (event: React.MouseEvent) => {
    event.stopPropagation();
    action?.();
  };

  return (
    <Card
      variant="outlined"
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        // Fill the grid row so neighbours line up whatever their content runs to.
        height: "100%",
        minWidth: 0,
        overflow: "hidden",
        transition:
          "background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease",
        "&:hover": {
          borderColor: "var(--color-border-strong)",
          boxShadow: "var(--shadow-md)",
          transform: "translateY(-2px)",
        },
        // focus-within as well as hover, or the tray is unreachable by keyboard.
        [`&:hover .${REVEAL}, &:focus-within .${REVEAL}`]: {
          opacity: 1,
          pointerEvents: "auto",
        },
        // The connect overlay flips visibility rather than pointer-events: its
        // wrapper spans the image and must never intercept the card's own click.
        [`&:hover .${CONNECT_REVEAL}, &:focus-within .${CONNECT_REVEAL}`]: {
          opacity: 1,
          visibility: "visible",
        },
      }}
    >
      {/* A sibling of the action area, not a child: that area is a <button>
          and a button cannot nest one. */}
      <Box
        className={REVEAL}
        sx={{
          position: "absolute",
          zIndex: 1,
          top: "var(--space-3)",
          left: "var(--space-3)",
          opacity: 0,
          // Transparent has to mean untappable too, or a touch device gets an
          // invisible destructive target on the corner of every card.
          pointerEvents: "none",
          transition: "opacity 150ms ease",
          // Nothing reveals it where there is no hover, so it just stays.
          "@media (hover: none)": { opacity: 1, pointerEvents: "auto" },
        }}
      >
        <ActionTray size="small">
          <Tooltip title={t("Download as zip")}>
            <IconButton
              aria-label={t("Download as zip")}
              onClick={stop(downloadAllAssetsAsZip)}
            >
              <HugeiconsIcon icon={Download04Icon} size={18} />
            </IconButton>
          </Tooltip>
          {onEdit && !processing && (
            <Tooltip title={t("Edit details")}>
              <IconButton aria-label={t("Edit details")} onClick={stop(onEdit)}>
                <HugeiconsIcon icon={PencilEdit01Icon} size={18} />
              </IconButton>
            </Tooltip>
          )}
          {onDelete && !processing && (
            <Tooltip title={t("Delete receipt")}>
              <IconButton
                aria-label={t("Delete receipt")}
                onClick={stop(onDelete)}
              >
                <HugeiconsIcon icon={Delete02Icon} size={18} />
              </IconButton>
            </Tooltip>
          )}
        </ActionTray>
      </Box>

      <CardActionArea
        disabled={processing}
        onClick={onOpen}
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "stretch",
          textAlign: "left",
          width: "100%",
          // Takes the slack so the Connect button sits on one line across a row.
          flex: 1,
          padding: 0,
          gap: 0,
        }}
      >
        <Box
          sx={{
            position: "relative",
            aspectRatio: "1/1",
            overflow: "hidden",
            backgroundColor: "var(--color-bg-elevated)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isPdf ? (
            <Box
              component="img"
              src={PdfIcon}
              alt="PDF"
              sx={{ width: "100px", height: "100px", objectFit: "contain" }}
            />
          ) : hasImage ? (
            <Box
              component="img"
              src={firstAsset?.assetUrl}
              alt={t("Receipt")}
              sx={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "var(--radius-md)",
                backgroundColor: "var(--color-bg-brand-subtle)",
                color: "var(--color-text-brand)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <HugeiconsIcon icon={Image01Icon} size={24} />
            </Box>
          )}

          <Box
            sx={{
              position: "absolute",
              top: "var(--space-3)",
              right: "var(--space-3)",
            }}
          >
            <Chip
              tone={scanTone}
              size="sm"
              leading={
                processing ? (
                  <CircularProgress size={14} />
                ) : (
                  <StatusGlyph status={scanLabel} tone={scanTone} />
                )
              }
            >
              {t(scanLabel)}
            </Chip>
          </Box>

          {assetCount > 1 && (
            <Box
              sx={{
                position: "absolute",
                bottom: "var(--space-3)",
                left: "var(--space-3)",
              }}
            >
              <Chip
                tone="neutral"
                size="sm"
                leading={<HugeiconsIcon icon={Image01Icon} size={14} />}
              >
                {`x${assetCount}`}
              </Chip>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--space-1)",
            padding: "var(--space-4)",
            width: "100%",
            minWidth: 0,
            flex: 1,
          }}
        >
          {uploader && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "var(--space-2)",
                color: "var(--color-text-tertiary)",
              }}
            >
              <UserAvatar size="sm" user={uploader} />
              <Box
                component="span"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                <Typography variant="label">{fullName(uploader)}</Typography>
              </Box>
            </Box>
          )}

          {processing ? (
            <Typography variant="body1" component="span" noWrap>
              {firstAsset?.filename || t("Receipt")}
            </Typography>
          ) : scanFailed ? (
            <>
              <Typography
                variant="label"
                component="span"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {t("No details found")}
              </Typography>
              <Typography
                variant="body2"
                component="span"
                sx={{ color: "var(--color-text-warning)" }}
              >
                {t("Add details to enable connection")}
              </Typography>
            </>
          ) : (
            <>
              <Typography
                variant="body1"
                component="span"
                title={receipt.companyName || undefined}
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {receipt.companyName?.trim() || t("No details found")}
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: "var(--space-2)",
                  minWidth: 0,
                }}
              >
                {hasAmount && (
                  <Typography variant="label" component="span">
                    {formatCashFull(receipt.amount, receipt.currency)}
                  </Typography>
                )}

                <Typography
                  variant="body2"
                  component="span"
                  sx={{
                    marginLeft: "auto",
                    whiteSpace: "nowrap",
                    color: "var(--color-text-tertiary)",
                  }}
                >
                  {dateMissing
                    ? "-"
                    : dayjs(receipt.transactionDate).format("MMM D, YYYY")}
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </CardActionArea>

      {/* Connecting is what the library is FOR, so it earns a real control on
          the tile rather than a trip through the viewer. */}
      {onConnect && !processing && (
        <Box
          className={CONNECT_REVEAL}
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            // Mirrors the thumbnail's ratio, so the button lands on the image's
            // bottom edge without this sibling knowing the image's height. It
            // has to be a sibling: the card's action area is a button, and a
            // button cannot nest one.
            aspectRatio: "1/1",
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "center",
            padding: "var(--space-3)",
            zIndex: 1,
            // The wrapper is inert so the image stays clickable; only the
            // button takes events back.
            pointerEvents: "none",
            "& > *": { pointerEvents: "auto" },
            // `visibility` is not redundant next to `opacity: 0`: the children
            // above re-enable pointer events, so without it the invisible
            // Connect button still takes clicks across the whole thumbnail.
            // It has to be transitioned as well, or the flip to hidden is
            // instant and the fade-out is never seen.
            opacity: 0,
            visibility: "hidden",
            transition: "opacity 150ms ease, visibility 150ms ease",
            // Nothing reveals it where there is no hover, so it just stays.
            "@media (hover: none)": { opacity: 1, visibility: "visible" },
          }}
        >
          {/* An empty title renders no tooltip: disableHoverListener would
              only silence the mouse, leaving keyboard focus and touch to
              announce "details missing" over a Connect that works. */}
          <Tooltip title={scanFailed ? t("ReceiptConnectRequiresDetails") : ""}>
            {/* The span keeps the tooltip alive over a disabled button, and
                takes a tab stop only while the button cannot: a disabled
                button is not focusable, so without this the explanation is
                unreachable by keyboard. */}
            <span
              style={{ display: "inline-flex" }}
              tabIndex={scanFailed ? 0 : undefined}
            >
              <Button
                variant="primary-filled"
                disabled={scanFailed}
                onClick={stop(onConnect)}
              >
                {t("Connect")}
              </Button>
            </span>
          </Tooltip>
        </Box>
      )}
    </Card>
  );
};

export default ReceiptLibraryCard;
